import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import NewsRow from '@/components/feature/NewsRow';
import { newsList } from '@/mocks/news';
import { categories } from '@/mocks/site';

const filterKeys = ['全部', ...categories.map((c) => c.key)];

function Feed() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return newsList.filter((item) => {
      const matchCategory = activeCategory === '全部' || item.category === activeCategory;
      const matchKeyword =
        !kw ||
        item.title.includes(kw) ||
        item.summary.includes(kw) ||
        item.source.includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof newsList>();
    filtered.forEach((item) => {
      const list = map.get(item.date) || [];
      list.push(item);
      map.set(item.date, list);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '网报吃瓜快讯时间线',
      description:
        '网报吃瓜快讯时间线收录最新热点快讯，按分类与时间顺序整理，标注信源与热度。',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        {
          '@type': 'ListItem',
          position: 2,
          name: '快讯时间线',
          item: `${window.location.origin}/feed`,
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="快讯时间线 - 网报吃瓜快讯实时更新，信源与热度一目了然"
        description="网报吃瓜快讯时间线实时更新热点快讯，支持按影视、综艺、音乐、网络、品牌、科技分类筛选与关键词搜索，每条标注信源与热度。"
        keywords="网报吃瓜快讯,网报吃瓜资讯,快讯时间线,网报吃瓜新闻,网报吃瓜报道"
        canonicalPath="/feed"
        schema={schema}
      />

      <div className="w-full px-4 pt-24 md:px-8 md:pt-32">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '快讯时间线' }]} />

        <header className="mt-6 border-b border-background-200/80 pb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <i className="ri-broadcast-line" />
            实时更新
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
            网报吃瓜快讯时间线
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里按时间顺序收录每一条快讯，标注信源与热度。你可以先用分类快速缩小范围，再用关键词定位具体事件，点开标题即可进入对应专题查看完整脉络。
          </p>
        </header>
      </div>

      <div className="w-full px-4 py-8 md:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {filterKeys.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveCategory(key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  activeCategory === key
                    ? 'bg-primary-500 text-foreground-950'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700'
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题、摘要或信源"
              aria-label="搜索快讯"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-600">
          共找到 <strong className="text-foreground-950">{filtered.length}</strong> 条快讯
          {activeCategory !== '全部' && ` · ${activeCategory}`}
        </p>

        {grouped.length === 0 ? (
          <div className="mt-10 rounded-lg border border-background-200/80 bg-background-100 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-400" />
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有匹配的快讯
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词，或者切换到「全部」分类再看看。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActiveCategory('全部');
              }}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        ) : (
          <div className="mt-8">
            {grouped.map(([date, items]) => (
              <section key={date} className="mb-10">
                <div className="mb-6 flex items-center gap-3">
                  <span className="flex items-center gap-2 rounded-md bg-secondary-950 px-3 py-1.5 font-heading text-sm font-bold text-background-50">
                    <i className="ri-calendar-line" />
                    {date}
                  </span>
                  <span className="h-px flex-1 bg-background-300" />
                  <span className="text-xs text-foreground-500">{items.length} 条</span>
                </div>
                {items.map((item, index) => (
                  <NewsRow
                    key={item.id}
                    item={item}
                    showDate={false}
                    last={index === items.length - 1}
                  />
                ))}
              </section>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-lg bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-base font-bold text-foreground-950">
            想按事件看完整脉络？
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            快讯适合快速扫一遍，如果你更关心某件事的来龙去脉，可以进入专题库，那里把起因、扩散、回应和媒体报道差异都串好了。
          </p>
          <Link
            to="/topics"
            className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-route-line" />
            进入专题库
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}

export default Feed;